import DOMPurify from "dompurify";

/**
 * Sanitizes HTML content to prevent XSS attacks while allowing safe formatting tags.
 *
 * This function uses DOMPurify to strip potentially dangerous HTML/JavaScript
 * while preserving basic formatting tags like <br>, <b>, <i>, <em>, <strong>.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```typescript
 * const userInput = 'Hello<br />World<script>alert("xss")</script>';
 * const safe = sanitizeHtml(userInput);
 * // Returns: 'Hello<br />World'
 * ```
 */
export function sanitizeHtml(html: string | React.ReactElement): string {
  // If it's a ReactElement, we can't sanitize it - return empty string
  // This should not happen in practice for SearchListbox, but provides type safety
  if (typeof html !== "string") {
    console.warn(
      "sanitizeHtml received non-string input, returning empty string",
    );
    return "";
  }

  // Configure DOMPurify to allow only safe formatting tags
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["br", "b", "i", "em", "strong"],
    ALLOWED_ATTR: [], // No attributes allowed (prevents onclick, onerror, etc.)
    KEEP_CONTENT: true, // Keep text content even if tags are stripped
  });

  return clean;
}
